import styles from '../ProductPageLayout/ProductPageLayout.module.css'
import React, { FC, useState, useCallback, useRef } from 'react'
import { useEffect } from 'react'
import { ConfiguratorPageShell } from '~/components/ConfiguratorPageShell/ConfiguratorPageShell'
import { useMediaQuery } from '@mui/material'
import {
  ProductImageSelect,
  ProductImageSelectComponent,
} from '~/components/ProductPage/productTypeComponents/wardrobe/ProductImageSelect'
import {
  ProductDimensions,
  ProductDimensionsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductDimensions'
import {
  ProductColors,
  ProductColorsComponent,
} from '~/components/ProductPage/productTypeComponents/ProductColors'
import {
  ProductSelect,
  ProductSelectComponent,
} from '~/components/ProductPage/productTypeComponents/ProductSelect'
import {
  ProductFurniture,
  ProductFurnitureComponent,
  ProductFurniturePredefinedValue,
} from '~/components/ProductPage/productTypeComponents/ProductFurniture'
import { ProductSectionPredefinedValue } from '~/components/ProductPage/productTypeComponents/wardrobe/ProductSections'
import {
  ProductPrice,
  ProductPriceComponent,
} from '~/components/ProductPage/productTypeComponents/ProductPrice'
import { ProductGalleryComponent } from '~/components/ProductPage/productTypeComponents/ProductGallery'
import { FormattedMessage } from 'react-intl'
import { useCart } from '~/context/cartContext'
import { Dimension } from '../ProductListPage/products'
import { use3DFurnitureProps } from '~/hooks/use3DFurnitureProps'
import { DEFAULT_RACK } from './productTypes/rack'
import {
  FurnitureViewer,
  FurnitureViewerRef,
} from '../ThreeDModel/FurnitureViewer'
import { ProductGalleryColorsConfig } from '~/components/ProductPage/productTypeComponents/ProductGalleryColors'
import { useRouter } from '~/hooks/useRouter'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import { ShareConfigModal } from '~/components/ShareConfigModal/ShareConfigModal'
import {
  ProductRackColumns,
  ProductRackColumnsComponent,
} from '~/components/ProductPage/productTypeComponents/rack/ProductRackColumns'
import type { ButtonOptionsType } from '~/components/ButtonSelect/ButtonSelect'
import { ButtonSelect } from '~/components/ButtonSelect/ButtonSelect'
import {
  NavSection,
  NAV_ORDER,
  SECTION_LABELS,
  isNavSection,
} from '~/components/ProductPage/productTypeComponents/sectionRegistry'
import { RackQuoteProvider } from '~/context/rackQuoteContext'
import { useFurniture } from '~/context/furnitureContext'
import { findChipboardIdByColorName } from '~/utils/furnitureBySku'
import type { RackColumnConfiguration } from '~/types/rackConfigurationTypes'

export type ProductComponent =
  | ProductImageSelectComponent
  | ProductGalleryComponent
  | ProductDimensionsComponent
  | ProductColorsComponent
  | ProductSelectComponent
  | ProductFurnitureComponent
  | ProductPriceComponent
  | ProductGalleryColorsConfig
  | ProductRackColumnsComponent
export type PredefinedValue = {
  sections?: ProductSectionPredefinedValue
  imageSelect?: string
  imageCarousel?: string[]
  gallery?: string[]
  dimensions?: Dimension
  colors?: string
  galleryColors?: string
  select?: string
  furniture?: ProductFurniturePredefinedValue
  price?: number
  screenshot?: string
}
interface ProductPageProps {
  components: () => ProductComponent[]
  name: string
  values?: PredefinedValue
}

// Helpers
const filterNavigable = (components: ProductComponent[]) =>
  components.filter((c): c is Extract<ProductComponent, { type: NavSection }> =>
    isNavSection(c.type as string)
  )

export const ProductPage: FC<ProductPageProps> = ({
  components,
  name,
  values,
}) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'ro'
  const { addItem } = useCart()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [activeColumnTab, setActiveColumnTab] = useState(0)
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(
    null
  )
  const furnitureViewerRef = useRef<FurnitureViewerRef>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  // Get current config for share functionality
  const urlConfigCtx = useConfiguratorConfigOptional()

  // Handle tab change from UI to update 3D selection
  const handleTabChange = useCallback((index: number) => {
    setActiveColumnTab(index)
    setSelectedColumnIndex(index)
  }, [])

  const currentComponents = components()

  // ---------- DESKTOP renderer (unchanged stack) ----------
  const getComponent = (component: ProductComponent): React.ReactNode => {
    switch (component.type) {
      case 'imageSelect':
        return (
          <ProductImageSelect
            configuration={component}
            predefinedValue={values?.imageSelect ?? undefined}
          />
        )
      case 'dimensions':
        return (
          <ProductDimensions
            configuration={component}
            predefinedValue={values?.dimensions ?? undefined}
          />
        )
      case 'colors':
        return (
          <ProductColors
            configuration={component}
            predefinedValue={values?.colors ?? undefined}
          />
        )
      case 'select':
        return (
          <ProductSelect
            configuration={component}
            predefinedValue={values?.select ?? undefined}
          />
        )
      case 'furniture':
        return (
          <ProductFurniture
            configuration={{ ...component, furnitureType: 'rack' }}
            predefinedValue={values?.furniture ?? undefined}
            onOpeningTypeChange={handleOpeningTypeChange}
          />
        )
      case 'rackColumns':
        return (
          <ProductRackColumns
            configuration={component}
            activeColumnIndex={activeColumnTab}
            onActiveColumnChange={handleTabChange}
          />
        )
      default:
        return null
    }
  }

  // ---------- MOBILE: chips + core-only panel ----------
  // Build available sections (present + valid for current mode)
  const navComponents = filterNavigable(currentComponents).sort(
    (a, b) =>
      NAV_ORDER.indexOf(a.type as NavSection) -
      NAV_ORDER.indexOf(b.type as NavSection)
  )

  const [activeSection, setActiveSection] = useState<NavSection | null>(
    navComponents[0]?.type ?? null
  )

  // Update activeSection when navComponents changes
  useEffect(() => {
    if (navComponents.length > 0) {
      const currentActive = navComponents.find((c) => c.type === activeSection)
      if (!currentActive) {
        setActiveSection(navComponents[0]?.type ?? null)
      }
    } else {
      setActiveSection(null)
    }
  }, [navComponents, activeSection])

  // Automatically select column when entering rackColumns tab
  useEffect(() => {
    if (activeSection === 'rackColumns') {
      setSelectedColumnIndex((prevIndex) => {
        if (prevIndex === null) {
          setActiveColumnTab(0)
          return 0
        } else {
          setActiveColumnTab(prevIndex)
          return prevIndex
        }
      })
    } else if (activeSection === 'furniture') {
      setSelectedColumnIndex(null)
    }
  }, [activeSection])

  const renderCore = (type: NavSection) => {
    const comp = currentComponents.find((c) => c.type === type)
    if (!comp) return null

    switch (type) {
      case 'dimensions':
        return (
          <ProductDimensions
            configuration={comp as ProductDimensionsComponent}
            predefinedValue={values?.dimensions ?? undefined}
          />
        )
      case 'colors':
        return (
          <ProductColors
            configuration={comp as ProductColorsComponent}
            predefinedValue={values?.colors ?? undefined}
          />
        )
      case 'rackColumns':
        return (
          <ProductRackColumns
            configuration={comp as ProductRackColumnsComponent}
            activeColumnIndex={activeColumnTab}
            onActiveColumnChange={handleTabChange}
          />
        )
      case 'select':
        return (
          <ProductSelect
            configuration={comp as ProductSelectComponent}
            predefinedValue={values?.select ?? undefined}
          />
        )
      case 'furniture': {
        return (
          <ProductFurniture
            configuration={{
              ...(comp as ProductFurnitureComponent),
              furnitureType: 'rack',
            }}
            predefinedValue={values?.furniture ?? undefined}
            onOpeningTypeChange={handleOpeningTypeChange}
          />
        )
      }
      default:
        return null
    }
  }

  const priceComponent = currentComponents.find(
    (component) => component.type === 'price'
  )

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Extract all 3D props using shared hook (rack uses automatic column layout)
  const furniture3DProps = use3DFurnitureProps(
    currentComponents,
    values,
    DEFAULT_RACK,
    true, // isWardrobe = true (enables automatic column layout)
    'rack' // furnitureType
  )

  // Get the number of columns from rack columns component
  const rackColumnsComponent = currentComponents.find(
    (c) => c.type === 'rackColumns'
  ) as ProductRackColumnsComponent | undefined
  const selectedColumns = rackColumnsComponent?.selectedColumns ?? 1

  // Resolve the selected color to a backend chipboardId for the pricing quote.
  const furniture = useFurniture()
  const colorsComp = currentComponents.find((c) => c.type === 'colors') as
    | ProductColorsComponent
    | undefined
  const chipboardId = React.useMemo(
    () => findChipboardIdByColorName(furniture, colorsComp?.selectedColor),
    [furniture, colorsComp?.selectedColor]
  )

  // Auto-select nearest column when active column disappears due to width reduction
  useEffect(() => {
    // Check if selected column index is out of bounds
    if (
      selectedColumnIndex !== null &&
      selectedColumnIndex >= selectedColumns
    ) {
      const nearestIndex = selectedColumns - 1
      setSelectedColumnIndex(nearestIndex)
      setActiveColumnTab(nearestIndex)
    }
    // Also check active tab
    if (activeColumnTab >= selectedColumns) {
      const nearestIndex = selectedColumns - 1
      setActiveColumnTab(nearestIndex)
    }
  }, [selectedColumns, selectedColumnIndex, activeColumnTab])

  // Handle column click from 3D viewer to update active tab
  const handleColumnClick = useCallback((index: number | null) => {
    if (index !== null) {
      setActiveColumnTab(index)
      setSelectedColumnIndex(index)
    } else {
      setSelectedColumnIndex(null)
    }
  }, [])

  // Handle opening type change - close all doors when handle type changes
  const handleOpeningTypeChange = useCallback(() => {
    setSelectedColumnIndex(null)
  }, [])

  const heading = (
    <>
      {/* h2, not h1: the prerender-safe canonical h1 for this route is rendered
          by <SeoContentSection> (main.tsx), outside the lazy route boundary. */}
      <h2 className={styles.visuallyHiddenTitle}>
        <FormattedMessage id="meta.header.configurator.rack" />
      </h2>
      <h2 className={styles.title}>
        <FormattedMessage id={name} />
      </h2>
    </>
  )

  const priceSection = priceComponent ? (
    <div className={styles.priceCont}>
      <ProductPrice
        onAddItem={(price, quote) => {
          const screenshot =
            furnitureViewerRef.current?.captureScreenshot() || undefined
          addItem('rack', currentComponents, {
            ...(values ?? {}),
            price,
            screenshot,
            quote,
          })
        }}
        configuration={priceComponent}
        predefinedValue={values?.price ?? undefined}
        productName={name}
        product="rack"
      />
    </div>
  ) : null

  const controls = isMobile && activeSection && navComponents.length > 0 ? (
    <>
      <div className={styles.mobileNavChips}>
        <ButtonSelect<NavSection>
          options={
            navComponents.map((c) => ({
              value: c.type as NavSection,
              label: SECTION_LABELS[c.type as NavSection].id,
            })) as ButtonOptionsType<NavSection>[]
          }
          defaultSelected={activeSection}
          size="medium"
          className={styles.mobileNavChipsRow}
          onChange={(val) => setActiveSection(val as NavSection)}
        />
      </div>

      <div className={styles.mobileCorePanel}>
        {renderCore(activeSection)}
      </div>

      {(() => {
        const colorsComponent = navComponents.find((c) => c.type === 'colors')
        if (colorsComponent && colorsComponent.type !== activeSection) {
          return (
            <div key="colors-hidden" style={{ display: 'none' }}>
              {renderCore('colors')}
            </div>
          )
        }
        return null
      })()}
    </>
  ) : (
    <>
      {currentComponents.map((component, index) => (
        <div key={index + component.type}>{getComponent(component)}</div>
      ))}
    </>
  )

  return (
    <RackQuoteProvider
      sku="rack"
      chipboardId={chipboardId}
      width={furniture3DProps.width}
      height={furniture3DProps.height}
      depth={furniture3DProps.depth}
      plintHeight={furniture3DProps.currentPlintHeight}
      columns={furniture3DProps.columns}
      openingType={furniture3DProps.openingType}
      columnWidths={furniture3DProps.columnWidths}
      columnConfigurations={
        furniture3DProps.columnConfigurations as
          | RackColumnConfiguration[]
          | undefined
      }
    >
      <ConfiguratorPageShell
        viewer={
          <FurnitureViewer
            ref={furnitureViewerRef}
            {...furniture3DProps}
            selectedColumnIndex={selectedColumnIndex}
            onColumnClick={handleColumnClick}
            onShare={() => setIsShareModalOpen(true)}
            onExport={() => furnitureViewerRef.current?.exportModel()}
          />
        }
        heading={heading}
        priceSection={priceSection}
        controls={controls}
        showOrderProgress
        showInfoBar
        modals={
          urlConfigCtx?.config ? (
            <ShareConfigModal
              isOpen={isShareModalOpen}
              onClose={() => setIsShareModalOpen(false)}
              config={urlConfigCtx.config}
              product="rack"
              locale={locale}
            />
          ) : undefined
        }
      />
    </RackQuoteProvider>
  )
}
